/**
 * Auth API service - handles all /api/auth/* endpoints.
 */

import { API_BASE } from "./config";
import type {
  ChallengeResponse,
  LoginResponse,
  AuthTokenPair,
  CIP30DataSignature,
} from "@mintmatrix/core";

async function post<T>(
  path: string,
  body: unknown,
  token?: string,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
  } catch (err) {
    // Network error (backend unreachable, DNS failure, etc.)
    throw new Error(
      "Unable to reach the server. Please check your connection and try again.",
    );
  }

  let data: any;
  try {
    data = await res.json();
  } catch {
    throw new Error(`Server returned an invalid response (${res.status})`);
  }

  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data as T;
}

/**
 * Request a challenge nonce for wallet signing.
 */
export async function requestChallenge(
  address: string,
  pkh: string,
): Promise<ChallengeResponse> {
  return post("/api/auth/challenge", { address, pkh });
}

/**
 * Login with wallet signature.
 */
export async function loginWallet(params: {
  nonce: string;
  pkh: string;
  address: string;
  signature: CIP30DataSignature;
  walletVendor?: string;
  networkId: number;
  stakingKeyHash?: string;
}): Promise<LoginResponse> {
  return post("/api/auth/login/wallet", params);
}

/**
 * Login with OAuth provider (Google/Apple).
 */
export async function loginOAuth(params: {
  provider: "google" | "apple";
  idToken: string;
  networkId?: number;
}): Promise<LoginResponse> {
  return post("/api/auth/login/oauth", params);
}

/**
 * Complete login with 2FA code.
 */
export async function login2FA(
  tempToken: string,
  code: string,
): Promise<LoginResponse> {
  return post("/api/auth/login/2fa", { tempToken, code });
}

/**
 * Refresh the token pair.
 */
export async function refreshTokens(
  refreshToken: string,
): Promise<AuthTokenPair> {
  return post("/api/auth/refresh", { refreshToken });
}

/**
 * Logout (revoke current session).
 */
export async function logout(
  accessToken: string,
  sessionId?: string,
  refreshToken?: string,
): Promise<void> {
  await post("/api/auth/logout", { sessionId, refreshToken }, accessToken);
}

/**
 * Logout all sessions.
 */
export async function logoutAll(accessToken: string): Promise<void> {
  await post("/api/auth/logout/all", {}, accessToken);
}

/**
 * Confirm a pending email address.
 */
export async function confirmEmail(token: string): Promise<{
  success: boolean;
  email: string | null;
  accountId: string;
}> {
  return post("/api/auth/email/confirm", { token });
}
